// Enrollments.jsx
import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useLocation ,Link } from 'react-router-dom';
import axios from 'axios';
import { Tabs, Table, Typography, Spin, Alert, Select, Input, Button } from 'antd';

import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const { Title, Paragraph } = Typography;
const { TabPane } = Tabs;
const { Search } = Input;
const { Option } = Select;

function normalize(v) {
  if (v === null || v === undefined) return '';
  return String(v).trim().toUpperCase();
}

// try to extract month name from RegisteredBatch or RegisteredBatch-like strings
const monthFromRegisteredBatch = (str) => {
  if (!str) return '';
  // split by non-word characters
  const parts = String(str).split(/[^A-Za-z]+/).map(p => p.trim()).filter(Boolean);
  for (const p of parts) {
    if (/^[A-Za-z]+$/.test(p)) {
      return p.toUpperCase();
    }
  }
  return '';
};

function Enrollments() {
  const navigate = useNavigate();
  const location = useLocation();

  const token = localStorage.getItem('token');
  const email = localStorage.getItem('email');
  const role = localStorage.getItem('role');

  const base =
     (role === 'ADMIN') ? "/admin-home"
      : "/user-home";

      const fullPath = `${base}/dashboard-enrollments`

  // --- auth verify (unchanged) ---
  useEffect(() => {
    const verifyUser = async () => {
      if (!token) {
        navigate('/login');
        return;
      }

      try {
        const res = await axios.post(
          `${process.env.REACT_APP_API_URL}/user/check-user`,
          { email, role, token }
        );

        if (!res.data.success) {
          const errMsg = res?.data?.message || 'Please Login Again.';
          toast.error(errMsg);
          localStorage.removeItem('token');
          localStorage.removeItem('tempToken');
          localStorage.removeItem('email');
          localStorage.removeItem('fullName');
          localStorage.removeItem('role');
          navigate('/login');
        }
      } catch (error) {
        const msg = error?.response?.data?.message || error?.message || 'Please Login Again.';
        toast.error(msg);
        localStorage.removeItem('token');
        localStorage.removeItem('tempToken');
        localStorage.removeItem('email');
        localStorage.removeItem('fullName');
        localStorage.removeItem('role');
        navigate('/login');
      }
    };

    verifyUser();
  }, [navigate, location.pathname, token, email, role]);

  // --- data states ---
  const [students, setStudents] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [loadingProfessionals, setLoadingProfessionals] = useState(false);
  const [error, setError] = useState(null);

  // fetch data
  useEffect(() => {
    const fetchStudents = async () => {
      setLoadingStudents(true);
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/enrollment/all-student-enrolled-leads`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = Array.isArray(res.data) ? res.data : res.data.data || [];
        setStudents(data.map((row, idx) => ({ key: row.Email ?? `s-${idx}`, ...row })));
      } catch (err) {
        console.error('Failed to fetch student leads', err);
        setError('Failed to load student leads');
        toast.error('Failed to load student leads');
      } finally {
        setLoadingStudents(false);
      }
    };

    const fetchProfessionals = async () => {
      setLoadingProfessionals(true);
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/enrollment/all-professional-enrolled-leads`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = Array.isArray(res.data) ? res.data : res.data.data || [];
        setProfessionals(data.map((row, idx) => ({ key: row.Email ?? `p-${idx}`, ...row })));
      } catch (err) {
        console.error('Failed to fetch professional leads', err);
        setError('Failed to load professional leads');
        toast.error('Failed to load professional leads');
      } finally {
        setLoadingProfessionals(false);
      }
    };

    if (token) {
      fetchStudents();
      fetchProfessionals();
    }
  }, [token, location.key]);

  // ----------- Filters (shared) -----------
  const [filterFullNameText, setFilterFullNameText] = useState('');
  const [filterEmailText, setFilterEmailText] = useState('');

  // ----------- Student-specific filters -----------
  const [studentSources, setStudentSources] = useState([]); // multi
  const [studentRegisteredMonth, setStudentRegisteredMonth] = useState(null); // single month like 'OCTOBER'
  const [studentBatchDays, setStudentBatchDays] = useState([]); // multi
  const [studentEnrollmentType, setStudentEnrollmentType] = useState(null);
  // ----------- Professional-specific filters -----------
  const [profSources, setProfSources] = useState([]); // multi
  const [profYears, setProfYears] = useState([]); // multi
  const [profRegisteredMonth, setProfRegisteredMonth] = useState(null);
  const [profBatchDays, setProfBatchDays] = useState([]);
  const [profEnrollmentType, setProfEnrollmentType] = useState(null);
  // helper for name/email matching
  const matchesText = (value, query) => {
    if (!query) return true;
    if (!value) return false;
    return normalize(value).includes(normalize(query));
  };

  // ---------- Filtered lists using useMemo ----------
  const filteredStudents = useMemo(() => {
    if (!Array.isArray(students)) return [];
    return students.filter((r) => {
      // name/email
      if (!matchesText(r.FullName, filterFullNameText)) return false;
      if (!matchesText(r.Email, filterEmailText)) return false;

      // LeadSource
      if (studentSources.length) {
        const src = normalize(r.LeadSource);
        const allowed = studentSources.map(s => normalize(s));
        if (!allowed.includes(src)) return false;
      }


      // Enrollment Type (single)
if (studentEnrollmentType) {
  const v = normalize(r.EnrollmentType || '');
  if (v !== normalize(studentEnrollmentType)) return false;
}

      // Registered month
      if (studentRegisteredMonth) {
        const month = monthFromRegisteredBatch(r.RegisteredBatch); // e.g. OCTOBER
        if (!month) return false;
        if (normalize(month) !== normalize(studentRegisteredMonth)) return false;
      }

      // BatchDay
      if (studentBatchDays.length) {
        const bd = normalize(r.BatchDay);
        const allowed = studentBatchDays.map(s => normalize(s));
        if (!allowed.includes(bd)) return false;
      }

      return true;
    });
  }, [
    students,
    filterFullNameText,
    filterEmailText,
    studentSources,
    studentEnrollmentType,
    studentRegisteredMonth,
    studentBatchDays,
  ]);

  const filteredProfessionals = useMemo(() => {
    if (!Array.isArray(professionals)) return [];
    return professionals.filter((r) => {
      // name/email
      if (!matchesText(r.FullName, filterFullNameText)) return false;
      if (!matchesText(r.Email, filterEmailText)) return false;

      // LeadSource
      if (profSources.length) {
        const src = normalize(r.LeadSource);
        const allowed = profSources.map(s => normalize(s));
        if (!allowed.includes(src)) return false;
      }

     
      // Years of exp
      if (profYears.length) {
        const y = normalize(String(r.YearsOfExperience || r.YearsOfExp || ''));
        const allowed = profYears.map(s => normalize(String(s)));
        if (!allowed.includes(y)) return false;
      }

      // Registered month
      if (profRegisteredMonth) {
        const month = monthFromRegisteredBatch(r.RegisteredBatch);
        if (!month) return false;
        if (normalize(month) !== normalize(profRegisteredMonth)) return false;
      }

      if (profEnrollmentType) {
  const v = normalize(r.EnrollmentType || '');
  if (v !== normalize(profEnrollmentType)) return false;
}

      // BatchDay
      if (profBatchDays.length) {
        const bd = normalize(r.BatchDay);
        const allowed = profBatchDays.map(s => normalize(s));
        if (!allowed.includes(bd)) return false;
      }

      return true;
    });
  }, [
    professionals,
    filterFullNameText,
    filterEmailText,
    profSources,
    profYears,
    profRegisteredMonth,
    profEnrollmentType,
    profBatchDays,
  ]);

  // ----------- Table columns (kept your original) -----------
  const studentColumns = [
    { title: <span className="text-[17px] font-bold text-white">Full Name</span>, dataIndex: 'FullName', key: 'FullName', width: 240, ellipsis: true,
      render: (t) => <span className="font-bold whitespace-nowrap">{t ?? '-'}</span> },
    { title: <span className="text-[17px] font-bold text-white">Email</span>, dataIndex: 'Email', key: 'Email', width: 280, ellipsis: true,
      render: (t) => <span className="font-bold whitespace-nowrap">{t ?? '-'}</span> },
    { title: <span className="text-[17px] font-bold text-white">Phone Number</span>, dataIndex: 'PhoneNumber', key: 'PhoneNumber', width: 180, ellipsis: true,
      render: (t) => <span className="ml-2 whitespace-nowrap">{t ?? '-'}</span> },
    { title: <span className="text-[17px] font-bold text-white">Lead Category</span>, dataIndex: 'LeadCategory', key: 'LeadCategory', width: 160, ellipsis: true,
      render: (t) => <span className="ml-5 whitespace-nowrap">{t ?? '-'}</span> },
    { title: <span className="text-[17px] font-bold text-white">Lead Date</span>, dataIndex: 'LeadDate', key: 'LeadDate', width: 160,
      render: (d) => <span className="ml-5 font-bold text-center text-blue-600 whitespace-nowrap">{d ?? '-'}</span> },
    { title: <span className="text-[17px] font-bold text-white">Lead Source</span>, dataIndex: 'LeadSource', key: 'LeadSource', width: 160, ellipsis: true,
      render: (t) => <span className="ml-5 whitespace-nowrap">{t ?? '-'}</span> },
  
    { title: <span className="text-[17px] font-bold text-white">College Name</span>, dataIndex: 'CollegeName', key: 'CollegeName', width: 260, ellipsis: true,
      render: (t) => <span className="ml-5 whitespace-nowrap">{t ?? '-'}</span> },
    { title: <span className="text-[17px] font-bold text-white">Passed Out year</span>, dataIndex: 'PassedOutYear', key: 'PassedOutYear', width: 170, ellipsis: true,
      render: (t) => <span className="ml-5 whitespace-nowrap">{t ?? '-'}</span> },
     { title: <span className="text-[17px] font-bold text-white">Enrollment Type</span>, dataIndex: 'EnrollmentType', key: 'EnrollmentType', width: 260, ellipsis: true,
      render: (t) => <span className="font-bold text-blue-700 truncate whitespace-nowrap">{t ?? '-'}</span> },
    { title: <span className="text-[17px] font-bold text-white">Registered Batch</span>, dataIndex: 'RegisteredBatch', key: 'RegisteredBatch', width: 190, ellipsis: true,
      render: (t) => <span className="ml-4 font-bold text-green-700 whitespace-nowrap">{t ?? '-'}</span> },
    { title: <span className="text-[17px] font-bold text-white">Batch Day</span>, dataIndex: 'BatchDay', key: 'BatchDay', width: 140, ellipsis: true,
      render: (t) => <span className="ml-4 font-bold whitespace-nowrap">{t ?? '-'}</span> },
    { title: <span className="text-[17px] font-bold text-white">Negotiated Fee</span>, dataIndex: 'NegotiatedFee', key: 'NegotiatedFee', width: 180, ellipsis: true,
      render: (t) => <span className="ml-6 font-bold text-red-700 whitespace-nowrap">{t ?? '-'}</span> },
    { title: <span className="text-[17px] font-bold text-white">First Installment Paid Amount</span>, dataIndex: 'FirstInstallmentAmount', key: 'FirstInstallmentAmount', width: 280, ellipsis: true,
      render: (t) => <span className="ml-10 font-bold text-red-700 whitespace-nowrap">{t ?? '-'}</span> },
    { title: <span className="text-[17px] font-bold text-white">First Installment Date</span>, dataIndex: 'FirstInstallmentDate', key: 'FirstInstallmentDate', width: 230, ellipsis: true,
      render: (t) => <span className="font-bold text-red-700 whitespace-nowrap ml-9">{t ?? '-'}</span> },
    { title: <span className="text-[17px] font-bold text-white">Second Installment Paid Amount</span>, dataIndex: 'SecondInstallmentPaidAmount', key: 'SecondInstallmentPaidAmount', width: 310, ellipsis: true,
      render: (t) => <span className="font-bold text-red-700 ml-14 whitespace-nowrap">{t ?? '-'}</span> },
    { title: <span className="text-[17px] font-bold text-white">Second Installment Date</span>, dataIndex: 'SecondInstallmentDate', key: 'SecondInstallmentDate', width: 230, ellipsis: true,
      render: (t) => <span className="ml-8 font-bold text-red-700 whitespace-nowrap">{t ?? '-'}</span> },
  ];

  const professionalColumns = [
    { title: <span className="text-[17px] font-bold text-white text-center">Full Name</span>, dataIndex: 'FullName', key: 'FullName', width: 240, ellipsis: true,
      render: (t) => <span className="font-bold text-center truncate whitespace-nowrap">{t ?? '-'}</span> },
    { title: <span className="text-[17px] font-bold text-white text-center">Email</span>, dataIndex: 'Email', key: 'Email', width: 280, ellipsis: true,
      render: (t) => <span className="font-bold whitespace-nowrap">{t ?? '-'}</span> },
    { title: <span className="text-[17px] font-bold text-white">Phone Number</span>, dataIndex: 'PhoneNumber', key: 'PhoneNumber', width: 160, ellipsis: true,
      render: (t) => <span className="truncate whitespace-nowrap">{t ?? '-'}</span> },
    { title: <span className="text-[17px] font-bold text-white">Lead Category</span>, dataIndex: 'LeadCategory', key: 'LeadCategory', width: 160, ellipsis: true,
      render: (t) => <span className="ml-2 truncate whitespace-nowrap">{t ?? '-'}</span> },
    { title: <span className="text-[17px] font-bold text-white">Lead Date</span>, dataIndex: 'LeadDate', key: 'LeadDate', width: 140,
      render: (d) => <span className="ml-3 font-bold text-blue-600 whitespace-nowrap">{d || '-'}</span> },
    { title: <span className="text-[17px] font-bold text-white">Lead Source</span>, dataIndex: 'LeadSource', key: 'LeadSource', width: 170, ellipsis: true,
      render: (t) => <span className="ml-5 truncate whitespace-nowrap">{t ?? '-'}</span> },
   
    { title: <span className="text-[17px] font-bold text-white">Years Of Exp</span>, dataIndex: 'YearsOfExperience', key: 'YearsOfExperience', width: 150, ellipsis: true,
      render: (t) => <span className="truncate whitespace-nowrap ml-9">{t ?? '-'}</span> },
  
    { title: <span className="text-[17px] font-bold text-white text-center">Company Name</span>, dataIndex: 'CompanyName', key: 'CompanyName', width: 210, ellipsis: true,
      render: (t) => <span className="ml-8 truncate whitespace-nowrap">{t ?? '-'}</span> },
    { title: <span className="text-[17px] font-bold text-white">Enrollment Type</span>, dataIndex: 'EnrollmentType', key: 'EnrollmentType', width: 270, ellipsis: true,
      render: (t) => <span className="font-bold text-blue-700 truncate whitespace-nowrap">{t ?? '-'}</span> },
    { title: <span className="text-[17px] font-bold text-white">Registered Batch</span>, dataIndex: 'RegisteredBatch', key: 'RegisteredBatch', width: 190, ellipsis: true,
      render: (t) => <span className="ml-2 font-bold text-green-700 truncate whitespace-nowrap">{t ?? '-'}</span> },
    { title: <span className="text-[17px] font-bold text-white">Batch Day</span>, dataIndex: 'BatchDay', key: 'BatchDay', width: 160, ellipsis: true,
      render: (t) => <span className="ml-4 font-bold truncate whitespace-nowrap">{t ?? '-'}</span> },
    { title: <span className="text-[17px] font-bold text-white">Negotiated Fee</span>, dataIndex: 'NegotiatedFee', key: 'NegotiatedFee', width: 180, ellipsis: true,
      render: (t) => <span className="ml-6 font-bold text-red-700 whitespace-nowrap">{t ?? '-'}</span> },
    { title: <span className="text-[17px] font-bold text-white">First Installment Paid Amount</span>, dataIndex: 'FirstInstallmentAmount', key: 'FirstInstallmentAmount', width: 280, ellipsis: true,
      render: (t) => <span className="ml-12 font-bold text-red-700 whitespace-nowrap">{t ?? '-'}</span> },
    { title: <span className="text-[17px] font-bold text-white">First Installment Date</span>, dataIndex: 'FirstInstallmentDate', key: 'FirstInstallmentDate', width: 230, ellipsis: true,
      render: (t) => <span className="ml-12 font-bold text-red-700 whitespace-nowrap">{t ?? '-'}</span> },
    { title: <span className="text-[17px] font-bold text-white">Second Installment Paid Amount</span>, dataIndex: 'SecondInstallmentPaidAmount', key: 'SecondInstallmentPaidAmount', width: 310, ellipsis: true,
      render: (t) => <span className="ml-12 font-bold text-red-700 whitespace-nowrap">{t ?? '-'}</span> },
    { title: <span className="text-[17px] font-bold text-white">Second Installment Date</span>, dataIndex: 'SecondInstallmentDate', key: 'SecondInstallmentDate', width: 230, ellipsis: true,
      render: (t) => <span className="ml-12 font-bold text-red-700 whitespace-nowrap">{t ?? '-'}</span> },
  ];

  // ---------- Clear filters helpers ----------
  const clearAllFilters = () => {
    setFilterFullNameText('');
    setFilterEmailText('');
    setStudentSources([]);
    
    setStudentRegisteredMonth(null);
    setStudentBatchDays([]);
    setStudentEnrollmentType(null);
    setProfEnrollmentType(null);
    setProfSources([]);
  
    setProfYears([]);
    setProfRegisteredMonth(null);
    setProfBatchDays([]);
  };

  const clearStudentFilters = () => {
    setStudentSources([]);
   
     setStudentEnrollmentType(null);
    setStudentRegisteredMonth(null);
    setStudentBatchDays([]);
  };

  const clearProfessionalFilters = () => {
    setProfSources([]);
  
    setProfYears([]);
     setProfEnrollmentType(null);
    setProfRegisteredMonth(null);
    setProfBatchDays([]);
  };


   let juniorFilter = filteredStudents.filter(r => {
    return r.EnrollmentType === 'JUNIOR PENETRATION TESTER'
  })

  let seniorFilter = filteredStudents.filter(r => {
    return r.EnrollmentType === 'SENIOR PENETRATION TESTER'
  })

   let juniorFilterProfessionals = filteredProfessionals.filter(r => {
    return r.EnrollmentType === 'JUNIOR PENETRATION TESTER'
  })

  let seniorFilterProfessionals = filteredProfessionals.filter(r => {
    return r.EnrollmentType === 'SENIOR PENETRATION TESTER'
  })
  // ---------- UI ----------
  return (
    <div className="w-full px-4 py-10 mx-auto bg-white shadow-sm sm:px-6 lg:px-8 rounded-xl">
      <ToastContainer
        position="bottom-left"
        autoClose={5000}
        hideProgressBar={true}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
        className="!mb-6 !ml-4"
        toastClassName="!inline-flex !items-center !whitespace-nowrap !bg-black !text-white !px-4 !py-2 !rounded-lg !w-auto !min-w-0 !shadow-lg"
      />

      <Title level={3} className="!text-3xl !font-bold text-gray-800 tracking-tight mb-3 border-b border-gray-200 pb-2">
        Enrollment Leads Dashboard
      </Title>

      <Paragraph className="mb-6 text-lg leading-relaxed text-gray-800">
        Welcome to the <span className="font-semibold text-blue-600">Enrollment Leads Dashboard</span>. Use the filters below — there are shared search fields (Full Name / Email) and specific filters per tab.
      </Paragraph>
      
       <p className="text-lg text-slate-700">
                    You can access all lead details anytime from the{" "}
                    <Link
                      to={fullPath}
                      className="inline-flex items-center gap-2 px-3 py-1 text-lg font-semibold text-red-600 bg-gray-100 rounded-md hover:bg-gray-200"
                    >
                      Enrollments Dashboard
                    </Link>
                  </p>

      {error && (
        <Alert
          type="error"
          message="Error Loading Leads"
          description={error}
          showIcon
          closable
          className="mb-6"
          onClose={() => setError(null)}
        />
      )}

      {/* Shared search row */}
      <div className="flex items-center gap-4 mb-4">
        <div className="flex items-center gap-2">
          <label className="text-sm font-semibold text-gray-700 whitespace-nowrap">Full Name</label>
          <Search
            placeholder="Search Full Name"
            allowClear
            value={filterFullNameText}
            onChange={(e) => setFilterFullNameText(e.target.value)}
            onSearch={(v) => setFilterFullNameText(v)}
            style={{ minWidth: 280,borderColor: '#4b5563', borderWidth: '2px', borderRadius: '0.5rem', color: '#9ca3af' }}
            enterButton={false}
          />
        </div>

        <div className="flex items-center gap-2">
          <label className="text-sm font-semibold text-gray-700">Email</label>
          <Search
            placeholder="Search Email"
            allowClear
            value={filterEmailText}
            onChange={(e) => setFilterEmailText(e.target.value)}
            onSearch={(v) => setFilterEmailText(v)}
            style={{ minWidth: 280,borderColor: '#4b5563', borderWidth: '2px', borderRadius: '0.5rem', color: '#9ca3af' }}
            enterButton={false}
          />
        </div>

        <div className="flex items-center gap-2 ">
          <Button className="font-semibold text-black bg-blue-400 border border-gray-400 hover:bg-gray-500" onClick={clearAllFilters} type="default">Clear All Filters</Button>
        </div>
      </div>

      <div className="mt-6">
        <Tabs defaultActiveKey="students" destroyInactiveTabPane={false} className="[&_.ant-tabs-nav]:border-b-2 [&_.ant-tabs-nav]:border-gray-200">
          {/* STUDENTS */}
          <TabPane tab={<span className="text-lg font-bold text-gray-900">Student Leads {loadingStudents && <Spin size="small" className="ml-2" />}</span>} key="students">
            {/* Student filters */}
            <div className="flex flex-wrap items-center gap-4 mb-4">
              <div className="flex items-center gap-2">
                <label className="text-sm font-semibold text-gray-700">Lead Source</label>
                <Select mode="multiple" placeholder={<span style={{ color: '#4b5563' }}>Select Sourse</span>} value={studentSources} onChange={(v) => setStudentSources(v)} style={{ minWidth: 200,borderColor: '#4b5563', borderWidth: '2px', borderRadius: '0.5rem', color: '#9ca3af' }}
                  options={[
                    { label: 'Google', value: 'GOOGLE' },
                    { label: 'Meta', value: 'META' },
                     { label: 'Meta-Instagram', value: 'META-INSTAGRAM' },
             { label: 'Meta-Facebook', value: 'META-FACEBOOK' },
                    { label: 'LinkedIn', value: 'LINKEDIN' },
                    { label: 'Organic', value: 'ORGANIC' },
                     { label: 'Youtube', value: 'YOUTUBE' },
                      { label: 'Youtube-Infeed', value: 'YOUTUBE-YIF' },
                       { label: 'Youtube-Instream', value: 'YOUTUBE-YIS' },
                  ]}
                />
              </div>

             

            

              <div className="flex items-center gap-2">
                <label className="text-sm font-semibold text-gray-700">Registered Month</label>
                <Select placeholder={<span style={{ color: '#4b5563' }}>Any Month</span>} value={studentRegisteredMonth} onChange={(v) => setStudentRegisteredMonth(v)} allowClear style={{ minWidth: 140,borderColor: '#4b5563', borderWidth: '2px', borderRadius: '0.5rem', color: '#9ca3af' }}
                  options={[
                    { label: 'JANUARY', value: 'JANUARY' }, { label: 'FEBRUARY', value: 'FEBRUARY' }, { label: 'MARCH', value: 'MARCH' },
                    { label: 'APRIL', value: 'APRIL' }, { label: 'MAY', value: 'MAY' }, { label: 'JUNE', value: 'JUNE' },
                    { label: 'JULY', value: 'JULY' }, { label: 'AUGUST', value: 'AUGUST' }, { label: 'SEPTEMBER', value: 'SEPTEMBER' },
                    { label: 'OCTOBER', value: 'OCTOBER' }, { label: 'NOVEMBER', value: 'NOVEMBER' }, { label: 'DECEMBER', value: 'DECEMBER' },
                  ]}
                />
              </div>

              {/* Enrollment Type */}
                            <div className="flex items-center gap-2">
                              <label className="text-sm font-semibold text-gray-700">Enrollment Type</label>
                              <Select
                                placeholder={<span style={{ color: '#4b5563' }}>Any</span>}
                                value={studentEnrollmentType}
                                onChange={(v) => setStudentEnrollmentType(v || null)}
                                allowClear
                                style={{ minWidth: 220, borderColor: '#4b5563', borderWidth: '2px', borderRadius: '0.5rem', color: '#9ca3af' }}
                                options={[ { label: 'JUNIOR PENETRATION TESTER', value: 'JUNIOR PENETRATION TESTER' }, { label: 'SENIOR PENETRATION TESTER', value: 'SENIOR PENETRATION TESTER' } ]}
                              />
                            </div>

              <div className="flex items-center gap-2">
                <label className="text-sm font-semibold text-gray-700">Batch Day</label>
                <Select mode="multiple" placeholder={<span style={{ color: '#4b5563' }}>Batch Day</span>} value={studentBatchDays} onChange={(v) => setStudentBatchDays(v)} style={{ minWidth: 160,borderColor: '#4b5563', borderWidth: '2px', borderRadius: '0.5rem', color: '#9ca3af' }}
                  options={[
                    { label: 'Morning', value: 'MORNING' },
                    { label: 'Evening', value: 'EVENING' },
                  ]}
                />
              </div>

              <div >
                <Button className="font-semibold text-black bg-blue-400 border border-gray-400 hover:bg-gray-500" onClick={clearStudentFilters}>Clear Student Filters</Button>
              </div>
            </div>

            <div className="flex items-center justify-start mb-4 ">

             <div className="flex items-center gap-4 mb-5 mr-5">
  <div className="px-4 py-3 bg-blue-200 border border-gray-600 rounded-md shadow-sm">
    <div className="text-xs font-bold tracking-wide text-black uppercase">
      Total Leads
    </div>
    <div className="mt-1 text-4xl font-extrabold text-center text-black">
      {filteredStudents.length}
    </div>
  </div> 
</div>


<div className="flex items-center gap-4 mb-5 mr-5">
  <div className="px-4 py-3 bg-blue-200 border border-gray-600 rounded-md shadow-sm">
    <div className="text-xs font-bold tracking-wide text-black uppercase">
      Junior Pen Tester Leads
    </div>
    <div className="mt-1 text-4xl font-extrabold text-center text-black">
      {juniorFilter.length}
    </div>
  </div> 
</div> 

<div className="flex items-center gap-4 mb-5 mr-5">
  <div className="px-4 py-3 bg-blue-200 border border-gray-600 rounded-md shadow-sm">
    <div className="text-xs font-bold tracking-wide text-black uppercase">
      Senior Pen Tester Leads
    </div>
    <div className="mt-1 text-4xl font-extrabold text-center text-black">
      {seniorFilter.length}
    </div>
  </div> 
</div> 

</div>

            <Table
              dataSource={filteredStudents}
              columns={studentColumns.map(c => ({ ...c, className: " text-[15px] font-semibold text-gray-900 px-6 py-2" }))}
              rowKey={(r) => r.Email || r.key}
              loading={loadingStudents}
              pagination={{ pageSize: 10, total: filteredStudents.length }}
              scroll={{ x: 2600 }}
              className="font-semibold text-center border border-gray-200 rounded-lg shadow-sm gray-header-table"
            />
          </TabPane>

          {/* PROFESSIONALS */}
          <TabPane tab={<span className="text-lg font-bold text-gray-800">Professional Leads {loadingProfessionals && <Spin size="small" className="ml-2" />}</span>} key="professionals">
            {/* Professional filters */}
            <div className="flex flex-wrap items-center gap-4 mb-4">
              <div className="flex items-center gap-2">
                <label className="text-sm font-semibold text-gray-700">Lead Source</label>
                <Select mode="multiple" placeholder={<span style={{ color: '#4b5563' }}>Select Source</span>} value={profSources} onChange={(v) => setProfSources(v)} style={{ minWidth: 200,borderColor: '#4b5563', borderWidth: '2px', borderRadius: '0.5rem', color: '#9ca3af' }}
                  options={[
                    { label: 'Google', value: 'GOOGLE' },
                    { label: 'Meta', value: 'META' },
                     { label: 'Meta-Instagram', value: 'META-INSTAGRAM' },
             { label: 'Meta-Facebook', value: 'META-FACEBOOK' },
                    { label: 'LinkedIn', value: 'LINKEDIN' },
                    { label: 'Organic', value: 'ORGANIC' },
                     { label: 'Youtube', value: 'YOUTUBE' },
                      { label: 'Youtube-Infeed', value: 'YOUTUBE-YIF' },
                       { label: 'Youtube-Instream', value: 'YOUTUBE-YIS' },
                  ]}
                />
              </div>

             

              <div className="flex items-center gap-2">
                <label className="text-sm font-semibold text-gray-700">Years Of Exp</label>
                <Select mode="multiple" placeholder={<span style={{ color: '#4b5563' }}>Years</span>} value={profYears} onChange={(v) => setProfYears(v)} style={{ minWidth: 140,borderColor: '#4b5563', borderWidth: '2px', borderRadius: '0.5rem', color: '#9ca3af' }}
                  options={[1,2,3,4,5,6].map(n => ({ label: String(n), value: String(n) }))}
                />
              </div>

              <div className="flex items-center gap-2">
                <label className="text-sm font-semibold text-gray-700">Registered Month</label>
                <Select placeholder={<span style={{ color: '#4b5563' }}>Any Month</span>} value={profRegisteredMonth} onChange={(v) => setProfRegisteredMonth(v)} allowClear style={{ minWidth: 140,borderColor: '#4b5563', borderWidth: '2px', borderRadius: '0.5rem', color: '#9ca3af' }}
                  options={[
                    { label: 'JANUARY', value: 'JANUARY' }, { label: 'FEBRUARY', value: 'FEBRUARY' }, { label: 'MARCH', value: 'MARCH' },
                    { label: 'APRIL', value: 'APRIL' }, { label: 'MAY', value: 'MAY' }, { label: 'JUNE', value: 'JUNE' },
                    { label: 'JULY', value: 'JULY' }, { label: 'AUGUST', value: 'AUGUST' }, { label: 'SEPTEMBER', value: 'SEPTEMBER' },
                    { label: 'OCTOBER', value: 'OCTOBER' }, { label: 'NOVEMBER', value: 'NOVEMBER' }, { label: 'DECEMBER', value: 'DECEMBER' },
                  ]}
                />
              </div>

              {/* Enrollment Type */}
                            <div className="flex items-center gap-2">
                              <label className="text-sm font-semibold text-gray-700">Enrollment Type</label>
                              <Select
                                placeholder={<span style={{ color: '#4b5563' }}>Any</span>}
                                value={profEnrollmentType}
                                onChange={(v) => setProfEnrollmentType(v || null)}
                                allowClear
                                style={{ minWidth: 220, borderColor: '#4b5563', borderWidth: '2px', borderRadius: '0.5rem', color: '#9ca3af' }}
                                options={[ { label: 'JUNIOR PENETRATION TESTER', value: 'JUNIOR PENETRATION TESTER' }, { label: 'SENIOR PENETRATION TESTER', value: 'SENIOR PENETRATION TESTER' } ]}
                              />
                            </div>

              <div className="flex items-center gap-2">
                <label className="text-sm font-semibold text-gray-700">Batch Day</label>
                <Select mode="multiple" placeholder={<span style={{ color: '#4b5563' }}>Batch Day</span>} value={profBatchDays} onChange={(v) => setProfBatchDays(v)} style={{ minWidth: 160, borderColor: '#4b5563', borderWidth: '2px', borderRadius: '0.5rem', color: '#9ca3af' }}
                  options={[
                    { label: 'Morning', value: 'MORNING' },
                    { label: 'Evening', value: 'EVENING' },
                  ]}
                />
              </div>

              <div >
                <Button className="font-semibold text-black bg-blue-400 border border-gray-400 hover:bg-gray-50" onClick={clearProfessionalFilters}>Clear Professional Filters</Button>
              </div>
            </div>

   <div className="flex items-center justify-start mb-4 ">

             <div className="flex items-center gap-4 mb-5 mr-5">
  <div className="px-4 py-3 bg-blue-200 border border-gray-600 rounded-md shadow-sm">
    <div className="text-xs font-bold tracking-wide text-black uppercase">
      Total Leads
    </div>
    <div className="mt-1 text-4xl font-extrabold text-center text-black">
      {filteredProfessionals.length}
    </div>
  </div> 
</div>


<div className="flex items-center gap-4 mb-5 mr-5">
  <div className="px-4 py-3 bg-blue-200 border border-gray-600 rounded-md shadow-sm">
    <div className="text-xs font-bold tracking-wide text-black uppercase">
      Junior Pen Tester Leads
    </div>
    <div className="mt-1 text-4xl font-extrabold text-center text-black">
      {juniorFilterProfessionals.length}
    </div>
  </div> 
</div> 

<div className="flex items-center gap-4 mb-5 mr-5">
  <div className="px-4 py-3 bg-blue-200 border border-gray-600 rounded-md shadow-sm">
    <div className="text-xs font-bold tracking-wide text-black uppercase">
      Senior Pen Tester Leads
    </div>
    <div className="mt-1 text-4xl font-extrabold text-center text-black">
      {seniorFilterProfessionals.length}
    </div>
  </div> 
</div> 

</div>
            

            <Table
              dataSource={filteredProfessionals}
              columns={professionalColumns.map(c => ({ ...c, className: " text-[15px] font-semibold text-gray-900 px-4 py-2" }))}
              rowKey={(r) => r.Email || r.key}
              loading={loadingProfessionals}
              pagination={{ pageSize: 10, total: filteredProfessionals.length }}
              scroll={{ x: 2600 }}
              className="font-semibold text-center border border-gray-200 rounded-lg shadow-sm gray-header-table"
            />
          </TabPane>
        </Tabs>
      </div>
    </div>
  );
}

export default Enrollments;
